/**
 * Utility functions for locale-aware number formatting and parsing.
 */

/**
 * Returns the decimal separator character for a given locale.
 * E.g., ',' for 'da', 'de', 'fr', 'es', etc., and '.' for 'en'.
 */
export const getLocaleDecimalSeparator = (locale: string = "en"): string => {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
    const decimalPart = parts.find((p) => p.type === "decimal");
    return decimalPart ? decimalPart.value : ".";
  } catch {
    return ".";
  }
};

/**
 * Returns the thousands / grouping separator character for a given locale.
 */
export const getLocaleGroupingSeparator = (locale: string = "en"): string => {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1000);
    const groupPart = parts.find((p) => p.type === "group");
    return groupPart ? groupPart.value : ",";
  } catch {
    return ",";
  }
};

/**
 * Parses a numeric string or number into a standard JavaScript float number,
 * respecting the specified or detected locale decimal separator.
 *
 * Handles:
 * - Comma as decimal separator ('12,50' -> 12.5)
 * - Dot as decimal separator ('12.50' -> 12.5)
 * - European/US thousands grouping ('1.234,56' or '1,234.56' -> 1234.56)
 * - Negative numbers ('-12,50' -> -12.5)
 * - Numbers already passed as type `number`
 */
export const parseLocaleNumber = (
  value: string | number | null | undefined,
  _locale: string = "en",
): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (value === null || value === undefined) {
    return 0;
  }

  const str = String(value).trim();
  if (!str) {
    return 0;
  }

  const isNegative = str.startsWith("-");
  const unsignedStr = str.replace(/^-/, "");

  const hasComma = unsignedStr.includes(",");
  const hasDot = unsignedStr.includes(".");

  let normalized = unsignedStr;

  if (hasComma && hasDot) {
    // Both separators exist, determine decimal by order:
    // e.g. "1.234,56" (comma last -> comma is decimal)
    // e.g. "1,234.56" (dot last -> dot is decimal)
    const lastComma = unsignedStr.lastIndexOf(",");
    const lastDot = unsignedStr.lastIndexOf(".");
    if (lastComma > lastDot) {
      normalized = unsignedStr.replace(/\./g, "").replace(",", ".");
    } else {
      normalized = unsignedStr.replace(/,/g, "");
    }
  } else if (hasComma) {
    // Only comma exists -> treat comma as the decimal separator
    normalized = unsignedStr.replace(",", ".");
  } else if (hasDot) {
    // Only dot exists -> standard dot decimal (or numpad dot)
    normalized = unsignedStr;
  }

  // Remove any remaining characters that are not digits or dot
  normalized = normalized.replace(/[^\d.]/g, "");

  const parsed = parseFloat(normalized);
  if (Number.isNaN(parsed)) {
    return 0;
  }

  return isNegative ? -parsed : parsed;
};

/**
 * Formats a number or numeric string to match the specified locale's decimal format.
 * E.g., for 'da', 12.5 -> '12,5'.
 *
 * If a string is provided that is already partially typed (like '12,' or '12.00'),
 * it converts the decimal separator to the locale's separator without trimming trailing characters.
 */
export const formatLocaleNumber = (
  value: number | string | null | undefined,
  locale: string = "en",
  options?: {
    maxDecimals?: number;
    minDecimals?: number;
  },
): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const targetDecimal = getLocaleDecimalSeparator(locale);
  const otherDecimal = targetDecimal === "," ? "." : ",";

  if (typeof value === "string") {
    // If it's a string, swap any opposite decimal separator to targetDecimal
    let str = value.replace(
      new RegExp(`\\${otherDecimal}`, "g"),
      targetDecimal,
    );

    if (options?.maxDecimals !== undefined) {
      const parts = str.split(targetDecimal);
      if (parts.length === 2 && parts[1].length > options.maxDecimals) {
        str = `${parts[0]}${targetDecimal}${parts[1].slice(0, options.maxDecimals)}`;
      }
    }
    return str;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return "";
    }

    // Convert standard number to string
    const str = value.toString();
    const parts = str.split(".");
    let integerPart = parts[0];
    let decimalPart = parts[1] || "";

    if (
      options?.maxDecimals !== undefined &&
      decimalPart.length > options.maxDecimals
    ) {
      decimalPart = decimalPart.slice(0, options.maxDecimals);
    }

    if (
      options?.minDecimals !== undefined &&
      decimalPart.length < options.minDecimals
    ) {
      decimalPart = decimalPart.padEnd(options.minDecimals, "0");
    }

    if (decimalPart.length > 0) {
      return `${integerPart}${targetDecimal}${decimalPart}`;
    }

    return integerPart;
  }

  return "";
};
